import React from 'react';
import { Menu } from 'antd';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useAccessRights from '../users/hooks/useAccessRights';
import { useDispatch, useSelector } from 'react-redux';
import {
  getIsSettingEnabled,
  getLastAccountingLocation,
  getLastContactsLocation,
  getLastHRLocation,
  getLastProfileLocation,
  getLastProjectLocation,
  getLastSettingsLocation,
  RootReducerState,
} from '../../apps/main/rootReducer';
import { setLastProjectLocation } from '../projects/actions';
import { setLastContactLocation } from '../contacts/actions';
import { setLastAccountingLocation } from '../controlling/actions';
import { setLastHRLocation } from '../hr/actions';
import { setLastSettingsLocation } from '../settings/actions/lastSettingsLocation';
import { setLastProfileLocation } from '../profile/actions';
import classNames from 'classnames';
import { makePrioStyles } from '../../theme/utils';
import * as ConfigValues from '../../util/configValues';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const useStyles = makePrioStyles({
  root: {
    '&.ant-menu': {
      border: 'none',
    },
    '&.ant-menu-root.ant-menu-vertical': {
      boxShadow:
        '0 3px 6px -4px rgb(0 0 0 / 12%), 0 6px 16px 0 rgb(0 0 0 / 8%), 0 9px 28px 8px rgb(0 0 0 / 5%)',
    },
  },
  rootSubMenu: {
    '&.ant-menu': {
      border: 'none',
    },
    '&.ant-menu-root.ant-menu-vertical': {
      boxShadow: 'none',
    },
  },
  nextJsMenuItem: {
    '&.ant-menu-item': {
      width: '100%',
    },
    '& .ant-menu-title-content a': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '8px',
      width: '100%',
    },
    '&.ant-menu-item:hover .svg-inline--fa': {
      color: 'currentColor',
    },
    '& .svg-inline--fa': {
      flexShrink: 0,
    },
  },
  nextJsMenuItemText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
});

interface MenuMenuProps {
  className?: string;
  closeDropDown?: VoidFunction;
  selectedEntry?: string;
  isSubMenu?: boolean;
}

export const MainMenu: React.FC<MenuMenuProps> = (props) => {
  const classes = useStyles();
  const { closeDropDown, selectedEntry, isSubMenu, className } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const lastProjectLocation = useSelector(getLastProjectLocation);
  const lastContactsLocation = useSelector(getLastContactsLocation);
  const lastAccountingLocation = useSelector(getLastAccountingLocation);
  const lastHRLocation = useSelector(getLastHRLocation);
  const lastSettingsLocation = useSelector(getLastSettingsLocation);
  const lastProfileLocation = useSelector(getLastProfileLocation);
  const isLocal =
    ConfigValues.REACT_APP_API_REDIRECT_URL.includes('prio.localhost');
  const getNextJsUrl = (path: string) => {
    const baseUrl = isLocal
      ? 'https://next.dev.fc-digitalisierung.de/prio/app'
      : `https://next.${ConfigValues.REACT_APP_API_REDIRECT_URL.replace(/^https?:\/\//, '').replace(/^cra\./, '')}prio/app`;
    return `${baseUrl}/${path}`;
  };

  const tryNewInterfaceLink = getNextJsUrl('myPrio/user/userDashboard');

  const isHREnabled = useSelector<RootReducerState, boolean>((state) =>
    getIsSettingEnabled(state, 'General_HumanResourcesServicesEnabled')
  );

  const isFinanceEnabled = useSelector<RootReducerState, boolean>((state) =>
    getIsSettingEnabled(state, 'General_FinanceServicesEnabled')
  );

  const isBetaFeaturesEnabledForUser = useSelector<RootReducerState, boolean>(
    (state) => getIsSettingEnabled(state, 'General_BetaFeaturesEnabled')
  );

  const isAlphaFeaturesEnabledForUser = useSelector<RootReducerState, boolean>(
    (state) => getIsSettingEnabled(state, 'General_AlphaFeaturesEnabled')
  );

  const setLastSession = (path: string) => {
    path = path.replace(window.location.origin, '');
    switch (true) {
      case /^.*\/projects.*?$/.test(path):
        const isInDocuments = path.includes('documents');
        const isMe = path.includes('module/prio/projects/me');
        if (isInDocuments && !isMe) {
          const newPath = `${path.split('/documents')[0]}/documents`;
          dispatch(setLastProjectLocation(newPath));
        } else {
          dispatch(setLastProjectLocation(path));
        }
        break;
      case /^.*\/prio\/contacts.*?$/.test(path):
        dispatch(setLastContactLocation(path));
        break;
      case /^.*\/prio\/controlling.*?$/.test(path):
        dispatch(setLastAccountingLocation(path));
        break;
      case /^.*\/prio\/hr.*?$/.test(path):
        dispatch(setLastHRLocation(path));
        break;
      case /^.*\/prio\/settings.*?$/.test(path):
        dispatch(setLastSettingsLocation(path));
        break;
      case /^.*\/prio\/profile.*?$/.test(path):
        dispatch(setLastProfileLocation(path));
        break;
      default:
        break;
    }
  };

  const {
    showControllingModule,
    showHrModule,
    showPrioSettings,
    showProjectModule,
    showContactsModule,
  } = useAccessRights([
    'showControllingModule',
    'showHrModule',
    'showPrioSettings',
    'showUserProfile',
    'showProjectModule',
    'showContactsModule',
  ]);

  return (
    <Menu
      className={classNames(classes.root, className, {
        [classes.rootSubMenu]: isSubMenu,
      })}
      selectedKeys={[selectedEntry]}
      id={'prio-main-menu'}
    >
      {showProjectModule && (
        <Menu.Item key={'projects'}>
          <Link
            to={lastProjectLocation ?? '/module/prio/projects'}
            onClick={() => {
              setLastSession(window.location.href);
              closeDropDown();
            }}
          >
            {t('common:links.projects')}
          </Link>
        </Menu.Item>
      )}
      {showContactsModule && (
        <Menu.Item key={'contacts'}>
          <Link
            to={lastContactsLocation ?? '/module/prio/contacts'}
            onClick={() => {
              setLastSession(window.location.href);
              closeDropDown();
            }}
          >
            {t('common:links.contacts')}
          </Link>
        </Menu.Item>
      )}
      {showContactsModule && (
        <Menu.Item key={'profile'}>
          <Link
            to={lastProfileLocation ?? '/module/prio/profile'}
            onClick={() => {
              setLastSession(window.location.href);
              closeDropDown();
            }}
          >
            {t('common:links.profile')}
          </Link>
        </Menu.Item>
      )}
      {isFinanceEnabled && showControllingModule && (
        <Menu.Item key={'controlling'}>
          <Link
            to={lastAccountingLocation ?? '/module/prio/controlling'}
            onClick={() => {
              setLastSession(window.location.href);
              closeDropDown();
            }}
          >
            {t('common:links.controlling')}
          </Link>
        </Menu.Item>
      )}
      {isHREnabled && showHrModule && (
        <Menu.Item key={'hr'}>
          <Link
            to={lastHRLocation ?? '/module/prio/hr'}
            onClick={() => {
              setLastSession(window.location.href);
              closeDropDown();
            }}
          >
            {t('common:links.hr')}
          </Link>
        </Menu.Item>
      )}
      {showPrioSettings && (
        <Menu.Item key={'settings'}>
          <Link
            to={lastSettingsLocation ?? '/module/prio/settings'}
            onClick={() => {
              setLastSession(window.location.href);
              closeDropDown();
            }}
          >
            {t('common:links.settings')}
          </Link>
        </Menu.Item>
      )}
      {(isAlphaFeaturesEnabledForUser || isBetaFeaturesEnabledForUser) && (
        <Menu.Item key={'tryNewInterface'} className={classes.nextJsMenuItem}>
          <Link
            to={tryNewInterfaceLink}
            onClick={() => {
              closeDropDown();
            }}
          >
            <span className={classes.nextJsMenuItemText}>
              {t('common:links.tryNewInterface')}
            </span>
            <FontAwesomeIcon icon={['fal', 'chevron-right']} />
          </Link>
        </Menu.Item>
      )}
    </Menu>
  );
};
